import { ContactList } from "components/ui/ContactList";
import { Briefcase } from "lucide-react";
import { useProviders } from "context/ProvidersContext";
import { useEffect, useMemo } from "react";
import { useAuth } from "context/AuthContext";
import { formatTimestamp } from "utils/dateFormat";
import { contactListItemClasses } from "utils/contactListItemClasses";
import { ReplyMuteBadge } from "components/ReplyMuteBadge";
import type { ReplyMute } from "types/ReplyMute";

interface ProvidersListProps {
  onSelectContact: (contactId: number) => void;
  selectedContactId: number | null;
  condoId: number;
}

type ProviderRow = {
  id: number;
  name: string;
  subtitle?: string;
  contactName: string;
  serviceType: string;
  lastMessageAt?: string | null;
  hasUnread?: boolean;
  rowKind: "condo" | "other";
  replyMute?: ReplyMute | null;
};

function providerSubheader(contactName: string, serviceType: string): string {
  const parts = [contactName.trim(), serviceType.trim()].filter(Boolean);
  return parts.join(" • ");
}

export function ProvidersList({
  onSelectContact,
  selectedContactId,
  condoId,
}: ProvidersListProps) {
  const { providers, otherCondoProviders, loading, error, refresh, markContactAsRead } =
    useProviders();
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";

  const handleSelectContact = (contactId: number) => {
    markContactAsRead(contactId);
    onSelectContact(contactId);
  };

  useEffect(() => {
    refresh(condoId);
  }, [condoId, refresh]);

  const listItems: ProviderRow[] = useMemo(() => {
    const condoRows = providers.map((provider) => ({
      id: provider.contact.id,
      name: provider.name,
      subtitle: providerSubheader(
        provider.contact.name,
        provider.serviceType,
      ),
      contactName: provider.contact.name,
      serviceType: provider.serviceType,
      lastMessageAt: provider.lastMessageAt,
      hasUnread: provider.hasUnread,
      rowKind: "condo" as const,
      replyMute: provider.replyMute ?? null,
    }));
    const otherRows = otherCondoProviders.map((provider) => ({
      id: provider.contact.id,
      name: provider.name,
      subtitle: providerSubheader(
        provider.contact.name,
        provider.serviceType,
      ),
      contactName: provider.contact.name,
      serviceType: provider.serviceType,
      lastMessageAt: provider.lastMessageAt,
      hasUnread: provider.hasUnread,
      rowKind: "other" as const,
      replyMute: provider.replyMute ?? null,
    }));
    return [...condoRows, ...otherRows];
  }, [providers, otherCondoProviders]);

  const otherBuildingsLabel =
    locale === "es" ? "Otros edificios" : "Other buildings";

  return (
    <ContactList<ProviderRow>
      listId="providers"
      title={locale === "es" ? "Proveedores" : "Providers"}
      icon={<Briefcase className="w-4 h-4 mr-2" />}
      items={listItems}
      loading={loading}
      error={error}
      searchPlaceholder={
        locale === "es" ? "Buscar proveedores..." : "Search providers..."
      }
      selectedId={selectedContactId}
      onSelectItem={handleSelectContact}
      getItemName={(item) =>
        `${item.name} ${item.contactName} ${item.serviceType}`.trim()
      }
      countItemsForHeader={(item) => item.rowKind === "condo"}
      renderItem={(item, isSelected, { filteredItems, filteredIndex }) => {
        const muted = item.rowKind === "other";
        const isFirstOtherInFiltered =
          item.rowKind === "other" &&
          !filteredItems
            .slice(0, filteredIndex)
            .some((row) => row.rowKind === "other");
        return (
          <div>
            {isFirstOtherInFiltered && (
              <div className="px-2 pb-1 pt-2 text-xs font-medium text-gray-500">
                {otherBuildingsLabel}
              </div>
            )}
            <div
              className={contactListItemClasses(isSelected, { muted })}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div
                    className={`truncate text-xs lg:text-sm ${
                      item.hasUnread ? "font-bold" : "font-medium"
                    } ${muted ? "text-gray-500" : ""}`}
                  >
                    {item.name}
                  </div>
                  {item.subtitle && (
                    <div className="text-xs text-gray-500">{item.subtitle}</div>
                  )}
                  <ReplyMuteBadge mute={item.replyMute} locale={locale} />
                </div>
                {item.lastMessageAt && (
                  <div className="flex-shrink-0 text-xs text-gray-400">
                    {formatTimestamp(item.lastMessageAt, locale)}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }}
    />
  );
}
